/**
 * EnginePanel — sidebar panel showing Engine Server status overview.
 *
 * Sidebar with four sections:
 *   - Connection: Engine Server status card → click to open detail view
 *   - SESSIONS: active session summary (file, definitions, outputs)
 *   - EXECUTIONS: stats only (running / success / error counts + last duration)
 *   - PLUGINS: compact list with action & backend counts
 *
 * Full details are shown in EngineDetailView (main content area).
 */

import {
    Activity,
    AlertTriangle,
    Blocks,
    ChevronDown,
    ChevronRight,
    Cpu,
    FileText,
    Layers,
    Package,
    RefreshCw,
    Server,
    Wrench,
    X,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import type { PluginInfo } from "@magic-md/engine-client/protocol";
import type { ExtensionSourceInfo, ExtensionRuntimeType } from "@magic-md/extension-sdk";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { usePluginRegistryVersion } from "../hooks/usePluginUI";
import { cn } from "../lib/utils";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { extensionUIRegistry } from "../extensions/extension-ui-registry";
import { isExtensionActive } from "../extensions/extension-host";
import { useExtensionSidebarPanels } from "../extensions/useExtensionContributions";
import { useEditorStore } from "../store/editor-store";
import { useEngineOverviewStore } from "../store/engine-overview-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { frontendLog } from "../utils/frontend-logger";
import {
    ExtensionList,
    type ExtensionListItemModel,
} from "./ExtensionList";
import { PluginList, type PluginListItemModel } from "./PluginList";
import { SidebarCloseButton } from "./SidebarCloseButton";
import { SidecarContent } from "./SidecarPanel";

/* ── Component ── */

export function EnginePanel() {
    const t = useT("editor");
    const setShowEngineDetail = useEditorStore((s) => s.setShowEngineDetail);
    const showEngineDetail = useEditorStore((s) => s.showEngineDetail);
    const openFileFromPath = useEditorStore((s) => s.openFileFromPath);

    const enginePlugins = usePluginExecutionStore((s) => s.enginePlugins);
    const activeSessionId = usePluginExecutionStore((s) => s.sessionId);
    const status = useEngineOverviewStore((s) => s.status);
    const plugins = useEngineOverviewStore((s) => s.plugins);
    const sessions = useEngineOverviewStore((s) => s.sessions);
    const sidecars = useEngineOverviewStore((s) => s.sidecars);
    const loading = useEngineOverviewStore(
        (s) =>
            s.loadingScopes.status ||
            s.loadingScopes.plugins ||
            s.loadingScopes.sessions ||
            s.loadingScopes.sidecars,
    );
    const error = useEngineOverviewStore((s) => s.error);
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);
    const refreshNow = useEngineOverviewStore((s) => s.refreshNow);
    const closeSession = useEngineOverviewStore((s) => s.closeSession);

    useEffect(() => {
        return retainOverviewScopes(["status", "plugins", "sessions", "sidecars"]);
    }, [retainOverviewScopes]);

    const handleCloseSession = async (sessionId: string) => {
        try {
            await closeSession(sessionId);
        } catch (e) {
            frontendLog.error("Failed to close session", e, { source: "engine" });
        }
    };

    const allPlugins = enginePlugins.length > 0 ? enginePlugins : plugins;
    const extensions = useExtensionList();

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Engine
                </span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => {
                            void refreshNow(["status", "plugins", "sessions", "sidecars"]);
                        }}
                        disabled={loading}
                        className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-50"
                        title={t("engine.refresh")}
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                    </button>
                    <SidebarCloseButton />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto p-2 space-y-2">
                {/* Connection Status Card */}
                <button
                    type="button"
                    className={cn(
                        "w-full rounded-xl border px-3.5 py-3 text-left transition-all group",
                        showEngineDetail
                            ? "border-primary/30 bg-primary/5 shadow-sm shadow-primary/5"
                            : "border-border/40 hover:border-border/60 hover:bg-muted/20",
                    )}
                    onClick={() => setShowEngineDetail(true)}
                >
                    <div className="flex items-center gap-3">
                        <StatusDot alive={status.alive} ready={status.ready} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[11px] font-semibold text-foreground">
                                    Engine Server
                                </span>
                            </div>
                            <span className="text-[10px] text-muted-foreground/70 mt-0.5 block">
                                {!status.alive
                                    ? t("engine.status.notRunning")
                                    : !status.ready
                                        ? t("engine.status.starting")
                                        : t("engine.status.connected")}
                            </span>
                        </div>
                        <span
                            className={cn(
                                "text-[9px] font-medium px-1.5 py-0.5 rounded-full",
                                !status.alive
                                    ? "bg-muted/50 text-muted-foreground/50"
                                    : "theme-accent-chip",
                            )}
                            style={
                                status.alive
                                    ? getThemeAccentStyle(
                                        status.ready ? "success" : "warning",
                                    )
                                    : undefined
                            }
                        >
                            {!status.alive
                                ? t("engine.status.offline")
                                : status.ready
                                    ? t("engine.status.online")
                                    : t("engine.status.startingShort")}
                        </span>
                    </div>
                </button>

                {error ? (
                    <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                        <AlertTriangle size={12} className="text-destructive shrink-0" />
                        <span className="text-[10px] text-destructive">{error}</span>
                    </div>
                ) : null}

                {status.ready ? (
                    <>
                        {/* ── SESSIONS ── */}
                        <CollapsibleSection
                            icon={<Layers size={11} />}
                            text={t("engine.sections.sessions")}
                            badge={sessions.length}
                            actions={
                                sessions.length > 0 ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            for (const s of sessions) {
                                                handleCloseSession(s.sessionId);
                                            }
                                        }}
                                        className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground/30 hover:text-destructive transition-colors"
                                        title={t("engine.sessions.closeAll")}
                                    >
                                        <X size={11} />
                                    </button>
                                ) : undefined
                            }
                        >
                            <div className="px-3 py-2">
                                {sessions.length === 0 ? (
                                    <span className="text-[10px] text-muted-foreground/50">
                                        {t("engine.sessions.empty")}
                                    </span>
                                ) : (
                                    <div className="space-y-1">
                                        {sessions.map((s) => {
                                            const isActive = s.sessionId === activeSessionId;
                                            const fileName = s.filePath
                                                ? s.filePath.split("/").pop()
                                                : t("common.untitled");
                                            return (
                                                <div
                                                    key={s.sessionId}
                                                    className="flex items-center gap-1.5 text-[10px] group/session"
                                                >
                                                    <span
                                                        className={cn(
                                                            "w-1.5 h-1.5 rounded-full shrink-0",
                                                            isActive
                                                                ? "bg-primary"
                                                                : "bg-muted-foreground/30",
                                                        )}
                                                    />
                                                    <FileText
                                                        size={10}
                                                        className={cn(
                                                            "shrink-0",
                                                            isActive
                                                                ? "text-primary"
                                                                : "text-muted-foreground/40",
                                                        )}
                                                    />
                                                    <button
                                                        type="button"
                                                        className={cn(
                                                            "truncate flex-1 text-left hover:underline",
                                                            isActive
                                                                ? "text-foreground/80 font-medium"
                                                                : "text-foreground/60",
                                                        )}
                                                        onClick={() => {
                                                            if (s.filePath) {
                                                                openFileFromPath(s.filePath);
                                                            }
                                                        }}
                                                        title={s.filePath ?? t("engine.sessions.openFile")}
                                                    >
                                                        {fileName}
                                                    </button>
                                                    <span className="text-muted-foreground/40 shrink-0 text-[9px]">
                                                        {s.unitCount}u · {s.outputCount}o
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCloseSession(s.sessionId);
                                                        }}
                                                        className="shrink-0 p-0.5 rounded hover:bg-destructive/10 text-muted-foreground/30 hover:text-destructive transition-colors opacity-0 group-hover/session:opacity-100"
                                                        title={t("engine.sessions.close")}
                                                    >
                                                        <X size={10} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </CollapsibleSection>

                        {/* ── PLUGINS: collapsible embedded panel ── */}
                        <CollapsibleSection
                            icon={<Package size={11} />}
                            text={t("engine.sections.plugins")}
                            badge={allPlugins.length}
                        >
                            <EnginePluginsContent plugins={allPlugins} />
                        </CollapsibleSection>

                        {/* ── SIDECARS: collapsible embedded panel ── */}
                        <CollapsibleSection
                            icon={<Server size={11} />}
                            text={t("engine.sections.sidecars")}
                            badge={`${sidecars.filter((s) => s.alive).length}/${sidecars.length}`}
                        >
                            <SidecarContent />
                        </CollapsibleSection>

                        {/* ── EXTENSIONS (client-side) ── */}
                        <CollapsibleSection
                            icon={<Blocks size={11} />}
                            text={t("engine.sections.extensions")}
                            badge={extensions.length}
                        >
                            <EngineExtensionsContent extensions={extensions} />
                        </CollapsibleSection>

                        {/* ── MCP SERVER ── */}
                        <CollapsibleSection
                            icon={<Wrench size={11} />}
                            text={t("engine.sections.mcpServer")}
                            badge={<McpStatusBadge />}
                        >
                            <McpServerContent />
                        </CollapsibleSection>
                    </>
                ) : null}

                {!status.ready ? (
                    <CollapsibleSection
                        icon={<Blocks size={11} />}
                        text={t("engine.sections.extensions")}
                        badge={extensions.length}
                    >
                        <EngineExtensionsContent extensions={extensions} />
                    </CollapsibleSection>
                ) : null}
            </div>
        </div>
    );
}

/* ── Sub-components ── */

function StatusDot({ alive, ready }: { alive: boolean; ready: boolean }) {
    return (
        <div className="relative shrink-0">
            <div
                className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center",
                    !alive
                        ? "bg-muted/40"
                        : "theme-accent-chip",
                )}
                style={
                    alive
                        ? getThemeAccentStyle(ready ? "success" : "warning")
                        : undefined
                }
            >
                {!alive ? (
                    <Cpu size={14} className="text-muted-foreground/40" />
                ) : ready ? (
                    <Server
                        size={14}
                        className="theme-accent-text"
                        style={getThemeAccentStyle("success")}
                    />
                ) : (
                    <Activity
                        size={14}
                        className="animate-pulse theme-accent-text"
                        style={getThemeAccentStyle("warning")}
                    />
                )}
            </div>
            {alive && (
                <span
                    className={cn(
                        "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background",
                        "theme-accent-fill",
                        !ready && "animate-pulse",
                    )}
                    style={getThemeAccentStyle(ready ? "success" : "warning")}
                />
            )}
        </div>
    );
}

function EnginePluginsContent({ plugins }: { plugins: PluginInfo[] }) {
    const t = useT("editor");
    const selectedPluginName = useEditorStore((s) => s.selectedPluginName);
    const setSelectedPluginName = useEditorStore((s) => s.setSelectedPluginName);
    usePluginRegistryVersion();
    const items = useMemo<PluginListItemModel[]>(() => {
        return plugins.map((plugin) => {
            const ui = pluginUIRegistry.get(plugin.name);
            const hasUI = !!ui;
            const source = plugin.source ?? ui?.source;
            return {
                id: plugin.name,
                title: ui?.manifest.title ?? plugin.title ?? plugin.name,
                icon: plugin.icon ?? ui?.manifest.icon,
                description: ui?.manifest.description ?? plugin.description,
                version: source?.version ?? plugin.version ?? ui?.manifest.version,
                actionCount: plugin.actions.length,
                source,
                disabled: !hasUI,
                status: hasUI ? "ready" : "warning",
                searchText: [
                    plugin.name,
                    plugin.overview,
                    plugin.category,
                    ui?.manifest.name,
                    ui?.manifest.overview,
                    ui?.manifest.category,
                    ...(plugin.features ?? []),
                    ...(ui?.manifest.features ?? []),
                ].filter((value): value is string => !!value),
            };
        });
    }, [plugins]);

    if (plugins.length === 0) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground/50">
                {t("pluginManager.empty")}
            </div>
        );
    }

    return (
        <PluginList
            items={items}
            selectedId={selectedPluginName}
            onSelect={setSelectedPluginName}
            searchPlaceholder={t("installedPlugins.searchPlaceholder")}
            emptyMessage={t("pluginManager.empty")}
            noMatchesMessage={t("installedPlugins.noMatching")}
        />
    );
}

/* ── Extensions section ── */

interface ExtensionListItem {
    id: string;
    title: string;
    icon?: string;
    description?: string;
    version?: string;
    category?: string;
    runtimeType?: ExtensionRuntimeType;
    source?: ExtensionSourceInfo;
    features?: string[];
}

/** Reactive snapshot of installed extensions from the UI registry. */
function useExtensionList(): ExtensionListItem[] {
    const revision = useSyncExternalStore(
        (onChange) => extensionUIRegistry.subscribe(onChange),
        () => extensionUIRegistry.revision(),
        () => extensionUIRegistry.revision(),
    );
    return useMemo(() => {
        void revision;
        return [...extensionUIRegistry.getAll().values()].map((ext) => ({
            id: ext.manifest.id,
            title: ext.manifest.title ?? ext.manifest.name ?? ext.manifest.id,
            icon: ext.manifest.icon,
            description: ext.manifest.description,
            version: ext.manifest.version,
            category: ext.manifest.category,
            runtimeType: ext.manifest.runtime.type,
            source: ext.source,
            features: ext.manifest.features,
        }));
    }, [revision]);
}

function EngineExtensionsContent({ extensions }: { extensions: ExtensionListItem[] }) {
    const t = useT("editor");
    const panels = useExtensionSidebarPanels();
    const selectedExtensionId = useEditorStore((s) => s.selectedExtensionId);
    const setSelectedExtensionId = useEditorStore((s) => s.setSelectedExtensionId);
    const items = useMemo<ExtensionListItemModel[]>(() => {
        return extensions.map((ext) => ({
            id: ext.id,
            title: ext.title,
            icon: ext.icon,
            description: ext.description,
            category: ext.category,
            version: ext.version,
            runtimeType: ext.runtimeType,
            source: ext.source,
            active: isExtensionActive(ext.id),
            hasPanel: panels.some((panel) => panel.extensionId === ext.id),
            searchText: ext.features,
        }));
    }, [extensions, panels]);

    if (extensions.length === 0) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground/50">
                {t("engine.extensions.empty")}
            </div>
        );
    }

    return (
        <ExtensionList
            items={items}
            selectedId={selectedExtensionId}
            onSelect={setSelectedExtensionId}
            searchPlaceholder={t("extensionPanel.searchPlaceholder")}
            emptyMessage={t("engine.extensions.empty")}
            noMatchesMessage={t("extensionPanel.noMatching")}
        />
    );
}

function CollapsibleSection({
    icon,
    text,
    badge,
    actions,
    defaultOpen = false,
    children,
}: {
    icon: React.ReactNode;
    text: string;
    badge?: React.ReactNode;
    actions?: React.ReactNode;
    defaultOpen?: boolean;
    children: React.ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="rounded-lg border border-border/30 overflow-hidden">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-muted/30 transition-colors">
                <button
                    type="button"
                    className="flex items-center gap-1.5 flex-1 min-w-0 text-muted-foreground transition-colors hover:text-foreground"
                    onClick={() => setOpen(!open)}
                >
                    {open ? (
                        <ChevronDown
                            size={11}
                            className="text-muted-foreground/70 shrink-0"
                        />
                    ) : (
                        <ChevronRight
                            size={11}
                            className="text-muted-foreground/70 shrink-0"
                        />
                    )}
                    <span className="text-muted-foreground">{icon}</span>
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex-1 text-left">
                        {text}
                    </span>
                    {badge != null ? (
                        typeof badge === "object" ? badge : <span className="text-[9px] font-medium text-muted-foreground/70 tabular-nums">
                            {badge}
                        </span>
                    ) : null}
                </button>
                {actions && (
                    <div className="flex items-center shrink-0">{actions}</div>
                )}
            </div>
            {open ? (
                <div className="border-t border-border/20">{children}</div>
            ) : null}
        </div>
    );
}

function McpStatusBadge() {
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const isRunning = mcpStatus === "running";
    const isBusy = mcpStatus === "starting" || mcpStatus === "stopping";

    const dotAccent = isRunning
        ? "success"
        : isBusy
            ? "warning"
            : mcpStatus === "error"
                ? "error"
                : null;

    return (
        <span
            className={cn(
                "w-1.5 h-1.5 rounded-full shrink-0",
                dotAccent ? "theme-accent-fill" : "bg-muted-foreground/30",
                isBusy && "animate-pulse",
            )}
            style={dotAccent ? getThemeAccentStyle(dotAccent) : undefined}
        />
    );
}

function McpServerContent() {
    const t = useT("editor");
    const showMcpDetail = useEditorStore((s) => s.showMcpDetail);
    const setShowMcpDetail = useEditorStore((s) => s.setShowMcpDetail);
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const mcpConnectedClients = useEditorStore((s) => s.mcpConnectedClients);

    const isRunning = mcpStatus === "running";
    const isBusy = mcpStatus === "starting" || mcpStatus === "stopping";

    const statusLabel = isRunning
        ? t("status.running")
        : isBusy
            ? mcpStatus === "starting" ? t("status.starting") : t("status.stopping")
            : mcpStatus === "error"
                ? t("status.error")
                : t("status.stopped");

    const statusAccent = isRunning
        ? "success"
        : isBusy
            ? "warning"
            : mcpStatus === "error"
                ? "error"
                : null;

    return (
        <button
            type="button"
            className={cn(
                "w-full px-3 py-2 text-left transition-colors group",
                showMcpDetail ? "bg-primary/5" : "hover:bg-muted/20",
            )}
            onClick={() => setShowMcpDetail(true)}
        >
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-medium text-foreground">
                            magic-md
                        </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground/60 mt-0.5 block">
                        {isRunning && mcpConnectedClients > 0
                            ? t("engine.mcp.clientsConnected", {
                                count: mcpConnectedClients,
                            })
                            : "Model Context Protocol"}
                    </span>
                </div>
                <span
                    className={cn(
                        "text-[9px] font-medium px-1.5 py-0.5 rounded-full",
                        statusAccent ? "theme-accent-chip" : "bg-muted/50 text-muted-foreground/50",
                    )}
                    style={statusAccent ? getThemeAccentStyle(statusAccent) : undefined}
                >
                    {statusLabel}
                </span>
            </div>
        </button>
    );
}
